import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { PagingParams } from "../../types/pagination";
import { useNavigate, useLocation } from "react-router-dom";
import Paginator from "../common/Paginator";
import ConfirmDialog from "../common/ConfirmDialog";
import Table from "../common/Table";
import { useStore } from "../../app/stores/store";
import toast from "react-hot-toast";
import React from "react";
import { all_routes } from "../router/all_routes";
import { dialogFlags } from "../../constants/constants";
import TableFilterBar from "../common/TableFilterBar";
import LoadingSpinner from "../common/LoadingSpinner";
import TableReport from "../common/TableReport";
import { PUBLIC_API_URL } from "../../environment";

const ReportList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Single store destructuring
  const {
    bookingStore: {
      bookings,
      loadBookings,
      allBookings,
      deleteBooking,
      pagination,
      setSearchTerm,
      setPagingParams,
      clearBookings,
      setDateFilter,
      setServiceFilter,
      setStatusCanceled,
      setStatusCompleted,
      setStatusFilter,
      setBookingCodeFilter,
    },
    clientStore: {
      currentClientItem,
      getClientItem,
    },
    userStore,
  } = useStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingClientItem, setLoadingClientItem] = useState(false);
  const [invoiceNo, setBookingCode] = useState("");
  const [showRefundModal, setShowRefundModal] = useState(false);

  // When setting the booking
  const onOpenRefundModal = (booking: any) => {
    setSelectedBooking({
      ...booking,
      refundRequests: Array.isArray(booking.refundRequests)
        ? booking.refundRequests
        : [],
    });
    setShowRefundModal(true);
  };
  const closeRefundModal = () => {
    setShowRefundModal(false);
    setSelectedBooking(null);
  };
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);

  // Fetch client item on selectedId change with loading state
  useEffect(() => {
    if (selectedId) {
      setLoadingClientItem(true);
      getClientItem(selectedId).finally(() => setLoadingClientItem(false));
    }
  }, [selectedId, getClientItem]);

  const handleGetNext = (page: number) => {
    setPagingParams(new PagingParams(page, pageSize));
    loadBookings();
  };

  const handleSearch = () => {
    setSearchTerm(searchQuery);
    setBookingCodeFilter(invoiceNo.trim());

    setPagingParams(new PagingParams(1, pageSize));
    loadBookings();
  };

  const handleReset = () => {
    setSearchTerm("");
    setSearchQuery("");
    setDateFilter("", "");
    setServiceFilter("");
    setStatusFilter("");
    setBookingCode("");
    setPagingParams(new PagingParams(1, pageSize));
    loadBookings();
  };

  const handleDelete = async () => {
    if (selectedId) {
      await deleteBooking(selectedId);
      loadBookings();
      navigate(location.pathname, { replace: true });
    }
  };

  const handleComplete = async () => {
    if (selectedId) {
      await setStatusCompleted(selectedId);
      loadBookings();
      navigate(location.pathname, { replace: true });
    }
  };

  const handleCancel = async () => {
    if (selectedId) {
      // await setStatusCanceled(selectedId);
      loadBookings();
      navigate(location.pathname, { replace: true });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPagingParams(new PagingParams(1, newPageSize));
    loadBookings();
  };

  useEffect(() => {
    const handleClose = () => { };
    const modal = document.getElementById(dialogFlags.editDialog);
    modal?.addEventListener("hidden.bs.modal", handleClose);
    return () => modal?.removeEventListener("hidden.bs.modal", handleClose);
  }, []);

  useEffect(() => {
    if (!userStore.token) {
      clearBookings();
      navigate("/login");
      toast.error("Unauthorized");
    } else {
      loadBookings();
    }
  }, [userStore.token, loadBookings]);




  if (!bookings) return <LoadingSpinner />;

  return (
    <div className="col-lg-12">
      <div className="row">
        <div className="col-lg-12 d-flex">
          <div className="card book-card flex-fill mb-0 p-0">
            <TableFilterBar
              pagination={pagination}
              pageSize={pageSize}
              handlePageSizeChange={handlePageSizeChange}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleSearch={handleSearch}
              handleReset={handleReset}

            />
            <div className="flex flex-col card-body">
              <div className="table-responsive dashboard-table">
                <TableReport
                  dialogFlags={dialogFlags}
                  setSelectedId={setSelectedId}
                  exceptColumns={[
                    "id",
                    "aiResult",
                    "createDate",
                    "address",
                    "email",
                    "calculatedCenterFee",
                    "calculatedServicePrice",
                    "reason",
                    "processTime",
                    "carName",
                    "entryType",
                    "updateDate",
                    "isGccResident",
                    "nationalityName",
                    "clients",
                    "invoiceNo",
                    "bankCharge",
                    "customerName",
                    "phone",
                    "refundAmount",
                    "updatePaymentID",
                    "updatePaymentUserName",
                    "paymentType",
                    "bookingCode", 
                    "bookingDate",
                    "childPrice",
                    "partnerPrice",
                    "partnerChildPrice",
                    "airportUserPrice",
                    "airportUserChildPrice",
                    "hasNationalityPrice",
                    "partnerCenterFee",
                    "partnerChildCenterFee",
                    "qty",
                    "childCenterFee",
                    "createdById",
                   "exception",
                  "updatedBy"
                  ]}
                  columnOrder={[
                    "invoiceNo",
                    "createDate",
                    "serviceName",
                    "duration",
                    "adultsNumber",
                    "childrenNumber",
                    "servicePrice",
                    "centerFee",
                    "totalPrice",
                    "securityDeposit",
                    "paymentStatus",
                    "bookingStatus",
                    "createdBy",
                    "createdByRoles",
                    "refundRequests",
                  ]}
                  data={allBookings}
                  pageSize={pageSize}
                  rowsPerPageOptions={[10, 25, 50]}
                  routeUrl={all_routes.reportDashboard}
                  onOpenRefundModal={onOpenRefundModal}

                />
              </div>
              <div className="mx-auto pt-2">
                <Paginator handleGetNext={handleGetNext} pagination={pagination} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showRefundModal && selectedBooking && (
        <div className="modal-backdrop-custom">
          <div className="custom-modal">
            <div className="modal-header">
              <h5>Refund Details</h5>
              <button onClick={closeRefundModal} className="btn-close" />
            </div>
            <div className="modal-body">
              {Array.isArray(selectedBooking.refundRequests) && selectedBooking.refundRequests.length > 0 ? (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th>Price</th>
                      <th>Refunded Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBooking.refundRequests.map((refund: any, index: number) => (
                      <tr key={index}>
                        <td>{refund.remarks || "N/A"}</td>
                        <td>{`AED ${selectedBooking.servicePrice}`}</td>
                        <td>{`AED ${refund.requestedAmount?.toFixed(2) ?? "0.00"}`}</td>
                        <td>{refund.status === 1 ? "Refunded" : "Pending"}</td>
                        <td>{refund.createdAt ? new Date(refund.createdAt).toLocaleDateString() : "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No refund requests found for this booking.</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default observer(ReportList);